import { useExtendedBooking, useOptimisticBookingState } from '@core/hooks';
import { getReservationQueue } from '@core/hooks/useReservationParams';
import { Reservation } from '@core/models';
import React, { useCallback, useMemo } from 'react';
import {
  INACTIVE_TIME,
  cellHeight,
  cellSize,
  cellXSpacing,
  cellYSpacing,
  gridCellRows,
  perMinuteCellSize,
} from './defaults';
import moment from 'moment';
import RenderReservationCellCard from './RenderReservationCard';
import { Space } from 'antd';

const RenderGridBaseComponents = React.memo(() => {
  const { bookingSetting } = useOptimisticBookingState();
  const { selectedDayAllRestaurantReservations } = useExtendedBooking();

  const workingHours = useMemo(
    () => bookingSetting.closed_hour - bookingSetting.open_hour,
    [bookingSetting.open_hour, bookingSetting.closed_hour]
  );

  const getInnerReservationQueue = useCallback((): Reservation<any>[][] => {
    return getReservationQueue(
      selectedDayAllRestaurantReservations,
      bookingSetting.open_hour,
      bookingSetting.closed_hour
    );
  }, [selectedDayAllRestaurantReservations, bookingSetting.open_hour, bookingSetting.closed_hour]);

  const reservationQueue = getInnerReservationQueue();

  const gridRowCellComponents = (): React.ReactNode[] => {
    const cellComponents: React.ReactNode[] = [];
    let currentHour = 0;
    let nextValidHour = 0;
    while (currentHour < workingHours + INACTIVE_TIME) {
      const reservationRenderedComponent = () => {
        const currentReservationQueue = reservationQueue[currentHour];
        if (currentReservationQueue?.length > 0 && currentHour >= nextValidHour) {
          const reservation: Reservation<any> = currentReservationQueue.pop() as any;
          nextValidHour = currentHour + reservation.duration / (60 * 60);
          return (
            <div
              style={{
                height: '100%',
                position: 'absolute',
                padding: '5px 5px',
                left: perMinuteCellSize * moment(reservation.reserved_date).minutes(),
                width: perMinuteCellSize * (reservation.duration / 60),
                zIndex: 1,
              }}>
              <RenderReservationCellCard key={reservation.id} reservation={reservation} />
            </div>
          );
        }
      };
      cellComponents.push(
        <div
          style={{
            width: cellSize,
            height: cellHeight,
            position: 'relative',
            backgroundColor: '#f7f7f7',
            borderRadius: 5,
          }}>
          {reservationRenderedComponent()}
        </div>
      );
      currentHour++;
    }
    return cellComponents;
  };

  let rows = gridCellRows;
  const grid = [];
  while (rows) {
    grid.push(
      <Space size={cellXSpacing} style={{ display: 'flex' }}>
        {gridRowCellComponents()}
      </Space>
    );
    rows--;
  }
  return (
    <Space direction="vertical" size={cellYSpacing}>
      {grid}
    </Space>
  );
});

export default RenderGridBaseComponents;
